import { maxUserPhotoBytes } from '@smarton-pbx/shared';
import { Camera, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { Notice } from '../components/ui';
import { t } from '../i18n';

/**
 * Add, change or remove a person's photo: their own (no userId) or, for
 * admins, someone else's. Saves at once, apart from any form around it.
 */
export function PhotoControls({ userId = null, version, onChange }: { userId?: string | null; version: string | null; onChange: (version: string | null) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function run(action: () => Promise<{ photoVersion: string | null }>) {
    setError(undefined);
    setBusy(true);
    try {
      onChange((await action()).photoVersion);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function upload(file: File | undefined) {
    if (!file) return;
    if (file.size > maxUserPhotoBytes) {
      setError(t('Photos can be up to 15 MB.'));
      return;
    }
    void run(() => api.uploadPhoto(file, userId));
  }

  return (
    <div className="stack" style={{ gap: '0.5rem' }}>
      <div className="row">
        <label className="button secondary small">
          <Camera size={15} aria-hidden /> {version ? t('Change photo') : t('Add a photo')}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            disabled={busy}
            onChange={(e) => {
              upload(e.target.files?.[0]);
              // The same file again still counts as a choice.
              e.target.value = '';
            }}
            data-testid="photo-input"
          />
        </label>
        {version && (
          <button type="button" className="ghost small" disabled={busy} onClick={() => void run(() => api.removePhoto(userId))} data-testid="remove-photo">
            <Trash2 size={15} aria-hidden /> {t('Remove photo')}
          </button>
        )}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
    </div>
  );
}
